import {
    CoreLogger,
    CreateExtArgs,
    CreateExtensionArgument,
    createFragmentStore,
    createSnapshotChain,
    DalValue,
    debug,
    DmApis,
    DmStore,
    Extension,
    ExtensionAPI,
    type FragmentStore,
    LoggerDriver,
    Null,
    PAGE_FRAGMENT_TYPE,
    pointerUtils,
    SNAPSHOT_REMOVED_EVENT,
    SnapshotDal,
    SnapshotRemovedError,
    store as dmStore,
    TransactionRejectionError,
    Store
} from '@wix/document-manager-core'
import {
    classic,
    defaultAttempt,
    getReportableFromError,
    Notifier,
    ReportableError,
    ReportableWarning,
    retryTaskAndReport,
    saveErrors,
    serverSaveErrorCodes,
    taskWithRetries,
    withTimeout,
    synchronized
} from '@wix/document-manager-utils'
import type {
    DocumentServiceError,
    DocumentServiceErrorInfo,
    DSConfig,
    UserInfoObject
} from '@wix/document-services-types'
import _ from 'lodash'
import long, {Long} from 'long'
import {MULTILINGUAL_TYPES, SNAPSHOTS, THRESHOLD_OF_FRAGMENTS_TO_COMMIT} from '../../constants/constants'
import {
    AtomicTransaction,
    CreateRevisionResponse,
    CreateTransactionRequest,
    InitiatorType,
    PendingTransaction,
    Transaction,
    GetDocumentResponse
} from '../../types'
import {AsyncQueue, createQueue, QueueFunction} from '../../utils/asyncQueue'
import {Channel, ChannelEvents} from '../channelUtils/duplexer'
import type {DocumentServicesModelExtApi} from '../documentServicesModel'
import type {RMApi} from '../rendererModel'
import type {SafeRemovalError, SchemaExtensionAPI} from '../schema/schema'
import type {SnapshotApi, SnapshotExtApi} from '../snapshots'
import * as converter from './csaveConverter'
import {removeFromLoadStore} from './csaveDataRules'
import {
    convertToDSError,
    documentStoreErrors,
    FirstTransactionRejectionError,
    InvalidLastTransactionIdError,
    isNetworkError,
    isStaleTransactionError,
    makeReportable,
    MissingTransactionInServerPayloadError,
    StaleStateOnForeignTransactionError,
    TransactionAlreadyApproveError,
    FragmentThresholdWarning,
    isStaleVersionError
} from './csaveErrors'
import type {
    Approved,
    ApprovedNonEmpty,
    ApprovedGroupNonEmpty,
    BatchTransactionResult,
    BatchTransactions,
    ContinuousSaveServer,
    CreateRevArgs,
    CSaveHooks,
    Rejected,
    SaveTransactionResponse,
    StaleEditorEnvironment,
    TransactionResult
} from './csaveTypes'
import {DuplexerOrderCheck, reportTransactionId} from './duplexerOrderCheck'
import {EmptyCSaveExt} from './emptyCSaveExt'
import {longGt} from './long'
import {GetTransactionRes, MockCEditTestServer} from './MockCEditTestServer'
import {MockCSDuplexer} from './mockCSChannel'
import {
    Action,
    ActionOperation,
    CreateRevisionRequest,
    GetTransactionsResponse,
    SaveRequest,
    ServerCreateRevisionResponse,
    FragmentTypesFilter
} from './serverProtocolTypes'
import {tagsFromError, validateCSaveTransaction} from './validateCSaveTransaction'
import {EmptySnapshotMap} from './emptySnapshotMap'
import type {DistributorExtensionAPI} from '../distributor/distributor'
import type {HooksExtensionApi} from '../hooks/hooks'
import {SAVE_HOOKS} from './hooks'
import type {TransactionsDroppedMessage} from '../distributor/messages'
import {getTranslationItemKey} from '../../utils/translationUtils'
import {isHeadless} from '../../utils/dsConfigUtils'
import {createFragmentTypesFilterForLoadOnInit} from '../../utils/fragmentUtils'
import {cSaveNotifierSendToServerTimeoutLimit, cSaveNotifierTimeoutLimit} from './continuousSaveConfig'
import type {ContinuousSavePayloadBuilderExtensionAPI, CreateTransactionReq} from './continuousSavePayloadBuilder'
import {convertToClientCreateRevisionResponse} from './serverTypesMapper'
import {FRAGMENT_INDEX} from '../fragments/fragments'

const {createStore} = dmStore
const {getPointer} = pointerUtils

const ACTIONS_TO_AUTOSAVE_COUNT_DENOMINATOR = 15
const MAX_TRANSACTIONS_BASE = 300

const COLLATOR = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'})

export const actionCountToAutosaveActionCount = (actionCount: number): number =>
    _.floor(actionCount / ACTIONS_TO_AUTOSAVE_COUNT_DENOMINATOR)

export const CS_EVENTS = {
    CSAVE: {
        SITE_SAVED: 'SITE_SAVED',
        DO_CSAVE: 'DO_CSAVE',
        NON_RECOVERABLE_ERROR: 'CAVE_NON_RECOVERABLE_ERROR'
    },
    SAVE: {
        FINISHED_SAVING: 'FINISHED_SAVING'
    }
}

let log: LoggerDriver

const CSAVE_TAG = 'CSAVE_TAG'
const CSAVE_PENDING_TAG = 'CSAVE_PENDING_TAG'
const CSAVE_INTERACTION = 'csave'
const CSAVE_VALIDATION_INTERACTION = 'csave-validation'
const CSAVE_TRANSACTION_INTERACTION = 'csave-approval-rate'
const CSAVE_TRANSACTION_REJECTED = 'csave-transaction-rejected'
const CSAVE_PAYLOAD_SIZE = 'csave-payload-size'
const channelName = 'dm_transactions'

export interface UpdateSiteDTO {
    restrictedDataDelta?: any
    logsMarkers?: any //LogMarkers
    translationsDelta?: any
    dataDelta?: any
    cedit?: boolean
    metaSiteData?: {
        siteName: string
    }
    metaSiteActions?: {actions: {}[]; maybeBranchId?: string}
    wixCodeAppData?: {codeAppId: string}
    dataNodeIdsToDelete: any
    branchId: any
    lastTransactionId: string
    initiator: string
    pagesPlatformApplications: any
    id: string
    protectedPagesData: any | {}
    version: string
    routers: any | {}
    signatures: any
    revision: string
    appsStateDelta?: any
}

export interface PageInfo {
    id: string
    title: string
}

export interface CSaveApi extends ExtensionAPI {
    continuousSave: {
        createRevision(args: CreateRevArgs, dataToSave: UpdateSiteDTO): Promise<CreateRevisionResponse>
        getLastTransactionId(): string | undefined
        getLastSaveDate(): Promise<Date | undefined>
        initCSave(partialPages?: string[]): Promise<boolean>
        initFragmentsTransactions(): Promise<void>
        initHooks(saveHooksMap: CSaveHooks): void
        getWrappedHooks(hooks: CSaveHooks): CSaveHooks
        save(): Promise<void | SaveTransactionResponse>
        saveAndWaitForResult(): Promise<void>
        forceSaveAndWaitForResult(): Promise<void>
        setEnabled(enabled: boolean): void
        setSaving(isSaving: boolean): void
        getPagesAddedInRevision(): Promise<PageInfo[]>
        getPagesRemovedInRevision(): Promise<PageInfo[]>
        disableSaveDuringRequiredAndPrimary(shouldDisableSave: boolean): void
        shouldSave(): boolean
        isCSaveOpen(): boolean
        isCEditOpen(): boolean
        restoreRevision(revisionId: string, siteId: string, branchId?: string): Promise<any>
        // for development purposes
        getTransactions(
            afterTransactionId?: string,
            untilTransactionId?: string,
            branchId?: string,
            fragmentTypesFilter?: FragmentTypesFilter
        ): Promise<GetTransactionsResponse>
        getTransactionsFromLastRevision(
            untilTransactionId?: string,
            branchId?: string
        ): Promise<GetTransactionsResponse>
        getStore(
            branchId?: string,
            afterTransactionId?: string,
            untilTransactionId?: string,
            fragmentTypesFilter?: FragmentTypesFilter
        ): Promise<GetDocumentResponse>
        approveForeignTransaction(t: GetTransactionRes): Promise<void>
        deleteTx(): Promise<void>
        rejectNext(): void
        registerToTransactionApproved(cb: (txStore: Store) => Promise<void>): void
        registerToTransactionApprovedAfterRebase(cb: () => Promise<void>): void
        waitForResponsesToBeProcessed(): Promise<void>
        test(): CSaveTestApi
        onRevisionChange(): void
        isUnappliedTransactionThresholdPassed(): boolean
        getUnappliedTransactionsData(): {count: number; max: number}
        getActionCount(): number
        getUnappliedActionCountSinceLastRevision(): number
        getUnappliedTransactionCountSinceLastRevision(): number
        cSaveErrors: {
            convertToDSError(e: any, userInfo: UserInfoObject): DocumentServiceError
        }
        isLocalTransaction(transactionId: string): boolean
        waitForCurrentPendingCsaves(): Promise<void>
        createRevisionWithSuppressTransactions(): Promise<void>
        notifySessionWillExpire(): void
    }
}

export type CSaveExtApi = CSaveApi['continuousSave']

export class CSaveTestApi {
    constructor(private ext: CSaveExtension) {}

    approveTransaction(correlationId: string, transactionId: string) {
        // @ts-expect-error
        this.ext.duplexer?.simulateApproval(correlationId, transactionId)
    }

    simulateOutOfSync() {
        // @ts-expect-error
        this.ext.duplexer?.simulateSubscriptionSucceeded({isSynced: false})
    }

    getUnappliedTransactionsCount(): number {
        return this.ext.getUnappliedTransactionsCount()
    }

    getMaxUnappliedTransactionsCount(): number {
        return this.ext.getMaxUnappliedTransactionsCount()
    }
}

const getLastCsaveSnapshot = (snapshots: SnapshotApi): SnapshotDal =>
    snapshots.getLastSnapshotByTagName(CSAVE_TAG) ?? snapshots.getInitialSnapshot()

const strToLong = (str?: string | null): Long => (_.isEmpty(str) ? long.ZERO : long.fromString(str!))

/** All propertied marked optional since this is not a well-defined contract between server and client */
interface TransactionRejectionDuplexerPayload {
    correlationId?: string
    errorCode?: string
    reason?: string
}

const logVersionInfoUpdateWarning = (e: VersionInfoUpdateError, logger: CoreLogger) => {
    logger.captureError(
        new ReportableWarning({
            errorType: 'VersionInfoIgnoreRejection',
            message:
                'A VersionInfo transactions is about to be rejected. Ignoring it. It will be sent in the next csave',
            extras: {
                rejectionError: JSON.stringify(e)
            }
        })
    )
}

const logStaleVersionInfoUpdateWarning = (e: VersionInfoUpdateError, logger: CoreLogger) => {
    logger.captureError(
        new ReportableWarning({
            errorType: 'StaleVersionInfoUpdate',
            message: 'A VersionInfo transaction is about to be rejected because of the stale state.',
            extras: {
                rejectionError: JSON.stringify(e)
            }
        })
    )
}

const getPayloadTransactions = (payload: SaveRequest): PendingTransaction[] => {
    if (payload.atomicTransactions) {
        return _.flatMap(payload.atomicTransactions, atomicTransaction =>
            _.has(atomicTransaction, ['transaction'])
                ? [_.get(atomicTransaction, ['transaction'])]
                : _.get(atomicTransaction, ['transactionGroup', 'transactions'], [])
        )
    }
    return payload.transactions ?? []
}

export class VersionInfoUpdateError extends ReportableError {
    public reason: any

    constructor(reason: any) {
        super({
            message: 'Failed to update version info',
            extras: {
                reason: JSON.stringify(reason)
            },
            errorType: 'VersionInfoUpdateError'
        })

        Object.setPrototypeOf(this, VersionInfoUpdateError.prototype)
        this.reason = reason
    }

    static isVersionInfoUpdateError(error: any): error is VersionInfoUpdateError {
        return error.errorType === 'VersionInfoUpdateError'
    }

    static shouldThrowVersionInfoUpdateError(e: any, logger: CoreLogger, payload?: SaveRequest): boolean {
        const transactions = getPayloadTransactions(payload ?? {})
        const payloadContainsVersionInfoUpdate = _.some(transactions, tx =>
            // eslint-disable-next-line lodash/matches-shorthand
            _.some(tx.actions, action => action.id === 'versionInfo' && action.namespace === 'documentServicesModel')
        )

        if (payloadContainsVersionInfoUpdate && (isStaleTransactionError(e) || isStaleVersionError(e))) {
            logStaleVersionInfoUpdateWarning(e, logger)
            return false
        }

        return payloadContainsVersionInfoUpdate
    }
}

class CSaveExtension implements Extension {
    readonly name: string = 'continuousSave'
    readonly EVENTS: Record<string, any> = CS_EVENTS
    readonly dependencies: ReadonlySet<string> = new Set([
        'snapshots',
        'documentServicesModel',
        'rendererModel',
        'distributor'
    ])
    private saving: boolean = false
    private isSaveDisabledDuringRequiredAndPrimary: boolean = false
    private pendingIndex: number = -1
    private readonly hooks: CSaveHooks = {
        onDiffSaveStarted: _.noop,
        onDiffSaveFinished: _.noop,
        onPartialSaveStarted: _.noop,
        onPartialSaveFinished: _.noop,
        onRefreshRequired: _.noop
    }
    private emptySnapshotsMap = new EmptySnapshotMap()

    private isEnabled: boolean = true
    private isInitialized: boolean = false

    private firstCorrelationId: string | null = null
    private revisionTransaction: Long = long.ZERO
    private unappliedActionCountSinceLastRevision: number = 0
    private unappliedTransactionCountSinceLastRevision: number = 0
    private actionCountByCorrelationId: Record<string, number> = {}

    constructor(
        private readonly server: ContinuousSaveServer,
        private readonly config: DSConfig,
        private readonly maxTransactionsModifier: number
    ) {}

    getMaxUnappliedTransactionsCount(): number {
        return MAX_TRANSACTIONS_BASE + this.maxTransactionsModifier
    }

    getUnappliedTransactionsCount(): number {
        const last = strToLong(this.server.getLast())
        const result = last.sub(this.revisionTransaction)
        if (result.isNegative()) {
            return 0
        }
        return result.toNumber()
    }

    createExtensionAPI({coreConfig, dal, pointers, extensionAPI, eventEmitter}: CreateExtArgs): CSaveApi {
        const {snapshots} = extensionAPI as SnapshotExtApi
        const {siteAPI, rendererModel} = extensionAPI as RMApi
        const {siteAPI: dsSiteApi} = extensionAPI as DocumentServicesModelExtApi
        const {distributor} = extensionAPI as DistributorExtensionAPI
        const {hooks: extensionHooksApi} = extensionAPI as HooksExtensionApi
        const {logger, experimentInstance} = coreConfig

        const queue: AsyncQueue = createQueue({
            queueName: 'CSave',
            useTimeout: true,
            defaultTimeout: 1000 * 60 * 5
        })
        const {server, hooks, config} = this

        distributor?.subscribeToMessage<TransactionsDroppedMessage>('transactionsDropped', () => {
            log.info('transactionsDropped message received - triggering VERSION_RESTORED')
            hooks.onRefreshRequired?.('VERSION_RESTORED')
        })
        const deferredTimeoutMap: Map<string, number> = new Map()
        let duplexer: Channel | null = null
        let duplexerIsReconnecting: boolean = false
        let transactionApprovedCb: (txStore: Store) => Promise<void>
        let transactionApprovedAfterRebaseCb: () => Promise<void>
        let cachedFragmentTypesFilter: FragmentTypesFilter | undefined
        const orderCheck = new DuplexerOrderCheck('0', logger)
        const fragmentQuery = {allTypes: true}

        const cache: {
            transactions: any
            pagesAdded: PageInfo[]
            pagesRemoved: PageInfo[]
            wasCacheInitialized: boolean
        } = {
            transactions: {},
            pagesAdded: [],
            pagesRemoved: [],
            wasCacheInitialized: false
        }

        const getFragmentTypesFilter = (): FragmentTypesFilter | undefined => {
            if (cachedFragmentTypesFilter) {
                return cachedFragmentTypesFilter
            }
            cachedFragmentTypesFilter = createFragmentTypesFilterForLoadOnInit(config)
            return cachedFragmentTypesFilter
        }

        const isNewFromTemplate = (): boolean => siteAPI.isTemplate() && !dsSiteApi.getAutosaveInfo()?.shouldAutoSave

        const getStoreWithCache = synchronized(
            async (
                branchId: string,
                afterTransactionId: string,
                untilTransactionId?: string,
                fragmentTypesFilter?: FragmentTypesFilter
            ): Promise<GetDocumentResponse | null> => {
                if (!isHeadless(this.config) && isNewFromTemplate()) {
                    return null
                }
                const cacheKey = _.compact([
                    branchId,
                    afterTransactionId,
                    untilTransactionId,
                    fragmentTypesFilter?.specificFragmentTypes?.fragmentTypes?.length
                        ? fragmentTypesFilter?.specificFragmentTypes?.fragmentTypes?.join('-')
                        : undefined
                ]).join('^')
                if (cache.transactions[cacheKey]) {
                    //shallow clone, so it is not modified
                    return {...cache.transactions[cacheKey]}
                }
                const extras = {
                    branchId,
                    afterTransactionId,
                    untilTransactionId,
                    fragmentTypesFilter
                }
                logger.interactionStarted('dm-get-document-transactions', {extras})
                const result = await server.getStore(
                    branchId,
                    afterTransactionId,
                    untilTransactionId,
                    fragmentTypesFilter
                )
                logger.interactionEnded('dm-get-document-transactions', {extras})
                cache.transactions[cacheKey] = result
                //shallow clone, so it is not modified
                return {...result}
            }
        )

        const hasTransactionsSinceRevision = (
            transactionResult?: GetDocumentResponse | null
        ): transactionResult is GetDocumentResponse => !!transactionResult?.lastTransactionId

        const cachePagesAddedOrRemoved = synchronized(async () => {
            if (cache.wasCacheInitialized) {
                return
            }
            const lastTransactionId = dsSiteApi.getAutosaveInfo()?.lastTransactionId ?? '0'
            server.setLast(lastTransactionId)
            log.info(`lastTransactionId=${lastTransactionId}`)
            const branchId = dsSiteApi.getBranchId()

            const transactionResult = await getStoreWithCache(
                branchId,
                lastTransactionId,
                this.config.untilTransactionId,
                getFragmentTypesFilter()
            )
            if (!hasTransactionsSinceRevision(transactionResult)) {
                cache.wasCacheInitialized = true
                return
            }
            if (this.config.autosaveRestore === 'false') {
                if (transactionResult.lastTransactionId) {
                    server.setLastUpdatedOnServer(transactionResult.lastTransactionId!)
                }
                return
            }
            if (transactionResult.lastTransactionId) {
                server.setLast(transactionResult.lastTransactionId!)
                server.setLastSaveDate(transactionResult.lastTransactionDateCreated!)
            }

            const pagesFromLastRevision = _.keyBy(rendererModel.getInitialPageList().pages, 'pageId')

            _.forEach(transactionResult.actions, action => {
                if (action.op === 'ADD' && action.namespace === 'data' && action.value.type === 'Page') {
                    cache.pagesAdded.push({id: action.id!, title: action.value.title})
                }
                if (action.op === 'REMOVE' && action.namespace === 'data' && pagesFromLastRevision[action.id!]) {
                    cache.pagesRemoved.push({id: action.id!, title: pagesFromLastRevision[action.id!].title})
                }
            })
            cache.wasCacheInitialized = true
        })

        const getPagesAddedInRevision = async () => {
            if (cache.wasCacheInitialized) {
                return cache.pagesAdded.slice()
            }
            await cachePagesAddedOrRemoved()
            return cache.pagesAdded.slice()
        }

        const getPagesRemovedInRevision = async () => {
            if (cache.wasCacheInitialized) {
                return cache.pagesRemoved.slice()
            }
            await cachePagesAddedOrRemoved()
            return cache.pagesRemoved.slice()
        }

        const onRevisionChange = () => {
            this.revisionTransaction = strToLong(this.server.getLast())
            this.unappliedActionCountSinceLastRevision = 0
            this.unappliedTransactionCountSinceLastRevision = 0
        }

        const isUnappliedTransactionThresholdPassed = () => {
            const count = this.getUnappliedTransactionsCount()
            const max = this.getMaxUnappliedTransactionsCount()
            return count > max
        }

        const getUnappliedTransactionsData = () => {
            const count = this.getUnappliedTransactionsCount()
            const max = this.getMaxUnappliedTransactionsCount()
            return {count, max}
        }

        const initUnappliedTransactions = (transactionResult: GetDocumentResponse) => {
            this.revisionTransaction = strToLong(transactionResult.firstTransactionId)
            const count = this.getUnappliedTransactionsCount()
            const max = this.getMaxUnappliedTransactionsCount()
            log.info(`${count} unapplied csave/cedit transactions. Maximum is ${max}`)
        }

        const setUnappliedActionCountSinceLastRevision = (unappliedActions?: number) => {
            if (_.isNumber(unappliedActions)) {
                this.unappliedActionCountSinceLastRevision = unappliedActions
                log.info(`${this.unappliedActionCountSinceLastRevision} unapplied csave/cedit actions.`)
            } else {
                logger.interactionStarted('FailedSettingUnappliedActionCount', {
                    method: 'setUnappliedActionCountSinceLastRevision'
                })
            }
        }

        const setUnappliedTransactionCountSinceLastRevision = (unappliedTransactions?: number) => {
            this.unappliedTransactionCountSinceLastRevision = unappliedTransactions || 0
        }

        const reportFirstTransactionRejection = (correlationId: string): void => {
            logger.captureError(new FirstTransactionRejectionError(correlationId))
        }

        const handleProcessingTransactionError = (
            csaveOp: string,
            error: unknown,
            extras?: Record<string, string | boolean>
        ) => {
            const err = error as Error
            const baseExtras = error instanceof ReportableError ? error.extras : {}
            const baseTags = error instanceof ReportableError ? error.tags : {}
            const errorType = 'ERROR_PROCESSING_TRANSACTION'
            const e = getReportableFromError(err, {
                errorType,
                message: 'error when processing transaction',
                tags: {...baseTags, duplexer: true, csaveOp},
                extras: {
                    ...baseExtras,
                    ...extras,
                    reason: {
                        name: err.name,
                        message: err.message,
                        ...(error instanceof ReportableError ? {originalErrorType: error.errorType} : {})
                    },
                    isWixInstanceExpired: rendererModel.isWixInstanceExpired()
                }
            })

            logger.captureError(e, {tags: e.tags, extras: e.extras})
            this.hooks.onRefreshRequired?.('ERROR_PROCESSING_TRANSACTION')
        }

        const notifier = new Notifier<string, void, any>(
            logger,
            {
                onReject: (correlationId: string) => {
                    if (this.firstCorrelationId && this.firstCorrelationId === correlationId) {
                        reportFirstTransactionRejection(correlationId)
                    }
                    log.info(`Notifier rejected ${correlationId}`)
                },
                onResolve: (correlationId: string) => {
                    log.info(`Notifier approved ${correlationId}`)
                },
                onTimeout: onNotifierTimeout
            },
            cSaveNotifierTimeoutLimit
        )

        async function onNotifierTimeout(correlationId: string, cancelRejectOnTimeout: () => void) {
            try {
                logger.interactionStarted('tryingToLoadTransactionsOnDeferredTimeout', {
                    extras: {correlationId}
                })

                await processFullSyncWithRetries()

                if (!notifier.isRegistered(correlationId)) {
                    log.info(`${correlationId} was approved by server but Notifier reached Request timed out`)
                    cancelRejectOnTimeout()
                    logger.interactionEnded('tryingToLoadTransactionsOnDeferredTimeout', {
                        extras: {correlationId, isTransactionApproved: true}
                    })
                } else {
                    log.info(`Notifier rejected ${correlationId} Request timed out`)
                    deferredTimeoutMap.set(correlationId, Date.now())
                    logger.interactionEnded('tryingToLoadTransactionsOnDeferredTimeout', {
                        extras: {correlationId, isTransactionApproved: false}
                    })
                }
                if (duplexer && !duplexer?.connected) {
                    await reconnectToDuplexerAndSubscribeToChannel()
                }
            } catch (e) {
                handleProcessingTransactionError('loadTransactionsFailedOnDeferredTimeout', e as Error, {
                    correlationId
                })
            }
        }

        const rejectNotifierOnRemovedSnapshots = (droppedSnapshots: SnapshotDal[]) => {
            droppedSnapshots.forEach(snapshot => {
                notifier.reject(snapshot.id, new SnapshotRemovedError(snapshot.id))
            })
        }

        eventEmitter.on(SNAPSHOT_REMOVED_EVENT, _.partial(rejectNotifierOnRemovedSnapshots))

        const setEnabled = (enabled: boolean) => {
            this.isEnabled = enabled ?? true
        }

        const isSavePermitted = (): boolean => !this.config.disableSave

        const shouldAutoSaveFromDsSiteApi = (): boolean => dsSiteApi.getAutosaveInfo()?.shouldAutoSave

        const isCSaveEnabled = (): boolean => {
            const isDraftMode = dsSiteApi.getIsDraft()
            const {disableAutoSave} = this.config
            const isAutosaveOn = !disableAutoSave
            const result = isAutosaveOn && this.isEnabled && shouldAutoSaveFromDsSiteApi()
            return result && !isDraftMode
        }

        const shouldSave = (): boolean => isSavePermitted() && isCSaveEnabled()

        const getIdFromAction = (action: Action) => {
            const {namespace, id} = action

            if (namespace === MULTILINGUAL_TYPES.multilingualTranslations && /^[^\^]+\^[^\^]+\^[^\^]+/.test(id!)) {
                const [, lang, dataItemId] = id!.split('^')
                return getTranslationItemKey(lang, dataItemId)
            }

            return id
        }

        const setValueToStore = (store: DmStore, action: Action): DalValue => {
            const {namespace, id, value, op, fragmentType, metadata} = action
            const {fragmentInstance} = value?.metaData || metadata || {}
            const pointer = getPointer(id!, namespace!, {fragmentType: fragmentType || undefined, fragmentInstance})

            if (!value || (!action.basedOnSignature && !dal.hasSignature(pointer))) {
                store.set(pointer, value)
                return
            }

            if (!value.metaData && metadata) {
                value.metaData = metadata
            }

            store.set(pointer, value)
            const basedOnSignature = action.basedOnSignature ?? (op === ActionOperation.ADD ? undefined : null)
            store.set(dal.getBasedOnSignaturePointer(pointer), basedOnSignature)
        }

        const actionsToDalChanges = (actions: Action[]): Store => {
            const result = createFragmentStore()
            actions.forEach((action: Action) => {
                const {fragmentType} = action
                if (!fragmentType) {
                    throw new Error('no fragmentType in action')
                }
                if (fragmentType === FRAGMENT_INDEX.TYPE) {
                    logger.interactionEnded('allowedFragmentIndexOperation', {
                        extras: {action}
                    })
                }
                const fragmentStore = result as FragmentStore
                if (!fragmentStore.hasFragmentStore(fragmentType)) {
                    fragmentStore.setFragmentStore(fragmentType, createStore())
                }
                const targetStore = fragmentStore.getFragmentStore(fragmentType)!
                setValueToStore(targetStore, action)
            })
            return result
        }

        const fetchTransaction = async (payload: ApprovedNonEmpty | ApprovedGroupNonEmpty) => {
            log.info(`fetching remote transaction ${payload.correlationId}`)
            if (payload.transactionIds) {
                const last = payload.transactionIds.length - 1
                const lastTxIdBeforeGroup = (parseInt(payload.transactionIds[0], 10) - 1).toString()
                const txGroup = await this.server.getTransactions(
                    lastTxIdBeforeGroup,
                    payload.transactionIds[last],
                    dsSiteApi.getBranchId(),
                    {allTypes: true}
                )
                if (!txGroup.transactions) {
                    throw new MissingTransactionInServerPayloadError(
                        payload.correlationId,
                        payload.transactionIds.join(', ')
                    )
                }
                const mergedActions: Action[] = []
                txGroup.transactions?.forEach(transaction => {
                    if (transaction.actions) {
                        mergedActions.push(...transaction.actions)
                    }
                })
                const firstTx = txGroup.transactions?.at(0)
                const mergedGroupTx: Transaction = {
                    actions: mergedActions,
                    branchId: firstTx?.branchId,
                    dateCreated: firstTx?.dateCreated,
                    metadata: firstTx?.metadata,
                    transactionId: firstTx?.transactionId
                }
                return mergedGroupTx
            }
            const tx = await this.server.getTransaction(payload.transactionId!, dsSiteApi.getBranchId(), fragmentQuery)
            if (!tx?.transaction) {
                throw new MissingTransactionInServerPayloadError(payload.correlationId, payload.transactionId!)
            }
            return tx.transaction
        }

        const reportForeignVersionInfoChange = (foreignTxStore: Store) => {
            let store = foreignTxStore
            const fragmentStore = (foreignTxStore as FragmentStore).getFragmentStore(PAGE_FRAGMENT_TYPE)
            if (!fragmentStore) {
                ;(foreignTxStore as FragmentStore).setFragmentStore(PAGE_FRAGMENT_TYPE, createStore())
            }
            store = (foreignTxStore as FragmentStore).getFragmentStore(PAGE_FRAGMENT_TYPE)!
            const revision = store.get(pointers.save.getSiteRevision())
            const version = store.get(pointers.documentServicesModel.getSiteVersion())
            const notEmpty = _.negate(_.isEmpty)
            const hasChange = _.some([revision, version], notEmpty)
            if (hasChange) {
                logger.interactionEnded('versionInfoUpdate', {
                    tags: {
                        revision,
                        version,
                        source: 'foreign_transaction'
                    }
                })
            }
        }

        const processForeignActions = async (
            actions: Action[],
            txId: string | null,
            correlationId?: string,
            isUndoable?: boolean
        ) => {
            if (txId && !longGt(txId, server.getLast()!)) {
                log.info(`TransactionAlreadyApproveError correlationId=${correlationId} transactionId=${txId}`)
                logger.captureError(
                    new TransactionAlreadyApproveError({
                        lastTxId: `${server.getLast()}`,
                        transactionId: txId,
                        correlationId: `${correlationId}`,
                        source: 'processForeignTransactions'
                    })
                )
                return dal.getLastApprovedSnapshot().id
            }
            const txStore = actionsToDalChanges(actions)
            reportForeignVersionInfoChange(txStore)
            if (_.isFunction(transactionApprovedCb)) {
                await transactionApprovedCb(txStore)
            }
            const lastApproved = dal.getLastApprovedSnapshot()
            const snapshot = dal.rebaseForeignChange(txStore, lastApproved.id, correlationId, isUndoable)
            if (isUndoable) {
                snapshot.setAsUndoable()
            }
            const lastCSave = getLastCsaveSnapshot(snapshots)
            if (lastCSave === lastApproved) {
                dal.tagManager.addSnapshot(CSAVE_TAG, snapshot)
            }
            dal.approve(snapshot.id)
            if (_.isFunction(transactionApprovedAfterRebaseCb)) {
                await transactionApprovedAfterRebaseCb()
            }
            return snapshot.id!
        }

        const isEmptyTx = (payload: Approved) => _.isNull(payload.transactionId)

        const isEmptyGroupTx = (payload: Approved): boolean => !payload.transactionIds?.length

        const isNonEmptyTx = (payload: Approved): payload is ApprovedNonEmpty | ApprovedGroupNonEmpty => {
            if (payload.transactionId !== null) {
                return !isEmptyTx(payload)
            }
            return !isEmptyGroupTx(payload)
        }
        const isTransactionFromValidInitiator = (transaction: Transaction) => {
            const initiatorType = transaction?.metadata?.initiator?.initiatorType
            return initiatorType && [InitiatorType.DMS, InitiatorType.EDITOR_SERVER].includes(initiatorType)
        }

        const shouldProcessForeignTransaction = (transaction: Transaction) => {
            if (this.config.origin === classic) {
                return (
                    (this.config.cedit || this.config.acceptTransactionsFromDuplexer) &&
                    isTransactionFromValidInitiator(transaction)
                )
            }
            return true
        }

        const getTransientDataValue = (
            transientData: {key: string; value: string}[] | undefined,
            key: string
        ): string | undefined => {
            return transientData?.find(({key: k}) => k === key)?.value
        }

        const isUndoableForeignTransaction = (payload: Approved): boolean => {
            const {transientData} = payload
            if (!transientData) {
                return false
            }
            const isUndoable = getTransientDataValue(transientData, 'undoable') === 'true'
            const txEsi = getTransientDataValue(transientData, 'esi')
            const currentEsi = dsSiteApi.getEditorSessionId()
            return isUndoable && !!txEsi && !!currentEsi && txEsi === currentEsi
        }

        const processForeignApproved = async (payload: Approved, transaction?: Transaction) => {
            if (!isNonEmptyTx(payload)) {
                return
            }
            logger.interactionStarted('processForeignApproved', {
                extras: {
                    correlationId: payload.correlationId,
                    branchId: dsSiteApi.getBranchId()
                }
            })
            log.info(`processing remote transaction ${payload.correlationId}`)

            const tx = _.isEmpty(transaction) ? await fetchTransaction(payload) : transaction
            const isUndoable = isUndoableForeignTransaction(payload)

            this.unappliedActionCountSinceLastRevision += tx.actions?.length ?? 0
            this.unappliedTransactionCountSinceLastRevision += payload.transactionIds?.length || 1
            const txId =
                payload.transactionId ??
                (payload.transactionIds && payload.transactionIds.length > 0
                    ? payload.transactionIds[payload.transactionIds.length - 1]
                    : null)
            if (shouldProcessForeignTransaction(tx)) {
                await processForeignActions(tx.actions!, txId, payload.correlationId, isUndoable)
                processLastTxId(payload)
            } else {
                const staleStateError = new StaleStateOnForeignTransactionError(
                    payload.correlationId,
                    payload.transactionId ?? payload.transactionIds?.join(', ') ?? '',
                    tx?.metadata
                )
                logger.captureError(staleStateError)
                throw staleStateError
            }
            distributor.foreignTransactionApplied(payload.correlationId)
            logger.interactionEnded('processForeignApproved', {
                extras: {
                    correlationId: payload.correlationId,
                    transactionId: payload.transactionId ?? payload.transactionIds?.join(', ')
                }
            })
        }

        const getPendingUpTo = (upTo: SnapshotDal): string[] => {
            const pendingSnapshots = dal.getLastApprovedSnapshot()!.createSnapshotChainTo(upTo)
            return _.map(pendingSnapshots, 'id')
        }

        const pendingUpToId = (id: string): string[] => getPendingUpTo(dal._snapshots.findById(id)!)

        const pendingTransactions = (): string[] => getPendingUpTo(dal.getLastSnapshot()!)

        const isLocalTransaction = (transactionId: string) => {
            const pending = pendingTransactions()
            return _.some(pending, (id: string) => id === transactionId)
        }

        const endCSaveTransactionInteraction = (correlationId: string) => {
            if (this.config.cedit) {
                logger.interactionEnded(CSAVE_TRANSACTION_INTERACTION, {extras: {correlationId}})
            }
        }

        const checkIfTransactionTimedOut = (correlationId: string) => {
            const timeoutTime = deferredTimeoutMap.get(correlationId)
            if (timeoutTime) {
                const approvalTime = Date.now()
                const diffMs = approvalTime - timeoutTime
                logger.captureError(
                    new ReportableWarning({
                        errorType: 'ApproveCorrelationAfterTimeout',
                        message: `Got approval from duplexer after deferred timeout for correlationId ${correlationId}`,
                        tags: {
                            correlationId,
                            timeoutTime: new Date(timeoutTime).toISOString(),
                            approvalTime: new Date(approvalTime).toISOString(),
                            diffMs
                        }
                    })
                )
                deferredTimeoutMap.delete(correlationId)
            }
        }

        const approveEmptyTransactions = (correlationId: string, pendingSnapshots: string[]) => {
            const emptyTransactions = this.emptySnapshotsMap.get(correlationId)
            const txToApprove = _.uniq([...emptyTransactions, ...pendingSnapshots]).sort(COLLATOR.compare)
            const lastTransactionId = txToApprove[txToApprove.length - 1]

            dal.approve(lastTransactionId)
            txToApprove.forEach(id => {
                distributor.transactionApproved(id, correlationId)
                notifier.resolve(id)
            })
            checkIfTransactionTimedOut(lastTransactionId)

            this.emptySnapshotsMap.delete(correlationId)
        }

        const processLocalApproved = (payload: Approved) => {
            const {correlationId, transactionId, transactionIds} = payload
            endCSaveTransactionInteraction(correlationId)
            logger.interactionStarted('processLocalApproved', {
                extras: {
                    correlationId,
                    branchId: dsSiteApi.getBranchId()
                }
            })

            log.info(`processing local transaction ${correlationId}`)
            // also resolve up to correlationId
            if (this.actionCountByCorrelationId[correlationId]) {
                this.unappliedActionCountSinceLastRevision += this.actionCountByCorrelationId[correlationId]
                if (isUnappliedTransactionThresholdPassed()) {
                    logger.interactionStarted('unappliedThresholdPassed', {
                        extras: {
                            unappliedTransactionCount: this.unappliedTransactionCountSinceLastRevision,
                            maxTransactions: this.getMaxUnappliedTransactionsCount()
                        }
                    })
                    extensionHooksApi.executeHook(SAVE_HOOKS.UNAPPLIED_THRESHOLD_PASSED.createEvent({}))
                }
                this.unappliedTransactionCountSinceLastRevision += 1
            } else {
                logger.interactionStarted('FailedSettingUnappliedActionCount', {
                    correlationId,
                    method: 'processLocalApproved'
                })
            }

            const pendingSnapshots = pendingUpToId(correlationId)
            if (pendingSnapshots.find(id => id === correlationId) === undefined) {
                logger.captureError(
                    new ReportableWarning({
                        errorType: 'CorrelationNotInPending',
                        message: `Cannot find correlationId ${correlationId} in pendingSnapshots. May result in timeout from saveAndWait`,
                        tags: {correlationId, pendingSnapshots}
                    })
                )
            }

            approveEmptyTransactions(correlationId, pendingSnapshots)
            logger.interactionEnded('processLocalApproved', {
                extras: {
                    correlationId,
                    transactionId: transactionId ?? transactionIds?.join(', ')
                }
            })
        }

        const startCSaveTransactionInteraction = (correlationId: string) => {
            if (this.config.cedit) {
                logger.interactionStarted(CSAVE_TRANSACTION_INTERACTION, {extras: {correlationId}})
            }
        }

        const validateTxId = (id: string | null) => {
            if (_.isUndefined(id) || id === '' || id === '0') {
                logger.captureError(new InvalidLastTransactionIdError(id))
            }
        }

        const safeLongGt = (a?: string, b?: string) => a && b && longGt(a, b)

        const isFirstTransaction = () => {
            const last = this.server.getLast()
            // the first transaction could be numbered differently
            return last === dsSiteApi.getAutosaveInfo()?.lastTransactionId
        }

        const isGreaterThanLast = (transactionId: string) => safeLongGt(transactionId, this.server.getLast())

        const updateLastTxId = (transactionId: string | null) => {
            if (transactionId && (isFirstTransaction() || isGreaterThanLast(transactionId))) {
                this.server.setLast(transactionId)
                this.server.setLastSaveDate(new Date())
            }
        }

        function processLastTxId(payload: Approved): void {
            if (isNonEmptyTx(payload)) {
                if (payload.transactionIds) {
                    payload.transactionIds.forEach(id => {
                        validateTxId(id)
                        updateLastTxId(id)
                    })
                } else {
                    validateTxId(payload.transactionId)
                    updateLastTxId(payload.transactionId)
                }
            }
        }

        const getUnappliedActionCountSinceLastRevision = () => this.unappliedActionCountSinceLastRevision
        const getUnappliedTransactionCountSinceLastRevision = () => this.unappliedTransactionCountSinceLastRevision

        const processTransaction = async (payload: Approved, transaction?: Transaction): Promise<void> => {
            const isLocal = isLocalTransaction(payload.correlationId)
            logger.interactionStarted('processTransaction', {
                extras: {
                    isLocal,
                    transactionId: payload.transactionId ?? payload.transactionIds,
                    correlationId: payload.correlationId,
                    branchId: dsSiteApi.getBranchId()
                }
            })
            if (isLocal) {
                processLocalApproved(payload)
                processLastTxId(payload)
            } else {
                await processForeignApproved(payload, transaction)
            }
            logger.interactionEnded('processTransaction', {
                extras: {
                    isLocal,
                    transactionId: payload.transactionId ?? payload.transactionIds?.join(', '),
                    correlationId: payload.correlationId
                }
            })
        }

        //Transactions must be processed in the order that they were received, otherwise we might cause a corruption
        async function processTransactions(transactions: Transaction[]): Promise<void> {
            for (const transaction of transactions) {
                await processTransaction(
                    {
                        transactionId: transaction.transactionId!,
                        correlationId: transaction.metadata!.correlationId!
                    },
                    transaction
                )
            }
        }

        async function processFullSync() {
            const lastTransactionId = server.getLast()
            log.info('sync from lastTransactionId =', lastTransactionId)
            const branchId = dsSiteApi.getBranchId()
            logger.interactionStarted('processFullSync', {
                extras: {
                    lastTransactionId,
                    branchId,
                    time: new Date(),
                    isWixInstanceExpired: rendererModel.isWixInstanceExpired()
                }
            })
            const result = await server.getTransactions(lastTransactionId, undefined, branchId, fragmentQuery)
            await processTransactions(result.transactions!)
            logger.interactionEnded('processFullSync', {
                extras: {lastTransactionId, time: new Date()}
            })
        }

        async function processFullSyncWithRetries() {
            const timeout = 1000 * 60 * 0.5
            const timeoutErrorMessage = 'processFullSync timed out'
            const processFullSyncWithTimeout = withTimeout(processFullSync, timeout, {
                message: timeoutErrorMessage
            }) as () => Promise<void>
            const shouldRetry = (error: any) => {
                return error.message === timeoutErrorMessage
            }
            await taskWithRetries(processFullSyncWithTimeout, shouldRetry, 2, 1000)
        }

        const getProcessFullSync = (): QueueFunction => async (): Promise<void> => {
            try {
                await processFullSyncWithRetries()
            } catch (e) {
                handleProcessingTransactionError('processFullSync', e)
                throw e
            }
        }

        const getProcessApproved =
            (payload: Approved): QueueFunction =>
            async (): Promise<void> => {
                try {
                    logger.interactionStarted('processApproveTransaction', {
                        extras: {correlationId: payload.correlationId, isEmptyTransaction: isEmptyTx(payload)}
                    })
                    await processTransaction(payload)
                    logger.interactionEnded('processApproveTransaction', {
                        extras: {correlationId: payload.correlationId, isEmptyTransaction: isEmptyTx(payload)}
                    })
                } catch (e) {
                    handleProcessingTransactionError('processApproved', e as Error, {
                        transactionId: payload.transactionId ?? payload.transactionIds?.join(', ') ?? '',
                        correlationId: payload.correlationId
                    })
                    throw e
                }
            }

        const approveEmptyTransactionsAfterRejection = (
            correlationId: string,
            pendingSnapshots: string[],
            errorCode?: string
        ) => {
            pendingSnapshots.forEach(id => notifier.reject(id, new TransactionRejectionError(id, errorCode)))

            const emptyTransactions = this.emptySnapshotsMap.get(correlationId)
            const txToApprove = _.without(emptyTransactions, ...pendingSnapshots)
            if (!txToApprove.length) {
                return
            }

            txToApprove.forEach(id => notifier.resolve(id))
            const lastTxToApprove = txToApprove[txToApprove.length - 1]
            if (COLLATOR.compare(lastTxToApprove, correlationId) > 0) {
                dal.approve(lastTxToApprove)
            }
        }

        const notifyDMError = (payload?: TransactionRejectionDuplexerPayload) => {
            if (!payload) {
                return
            }

            const {correlationId, errorCode} = payload
            logger.captureError(
                new ReportableError({
                    errorType: 'RejectedTransaction',
                    message: `Got Rejection for local transaction ${correlationId} from duplexer`,
                    extras: {errorCode, originalError: payload}
                })
            )
            if (!errorCode || errorCode === documentStoreErrors.CONFLICT_DETECTED) {
                return
            }
            const userInfo = dal.get(pointers.documentServicesModel.getUserInfo())
            this.hooks.onDiffSaveFinished?.(convertToDSError(payload, userInfo))
        }

        const getProcessRejected =
            (correlationId: string, payload?: TransactionRejectionDuplexerPayload): QueueFunction =>
            (): void => {
                const pendingSnapshots = defaultAttempt([], pendingUpToId, correlationId)
                const isLocal = isLocalTransaction(correlationId)
                try {
                    if (isLocal) {
                        log.info(`processed reject for ${correlationId}`)
                        dal.reject(correlationId)
                    } else {
                        log.info(`ignoring remote reject for ${correlationId}`)
                    }
                } catch (e) {
                    handleProcessingTransactionError('processRejected', e as Error, {correlationId})
                    throw e
                } finally {
                    const snapshotsForReports = _.dropRight(pendingSnapshots, 1)
                    const hasRegisteredPending = snapshotsForReports.some(id => notifier.isRegistered(id))
                    logger.captureError(
                        new ReportableWarning({
                            errorType: 'PendingSnapshotRejection',
                            tags: {hasRegisteredPending},
                            message: 'Rejected pending snapshot'
                        })
                    )
                    approveEmptyTransactionsAfterRejection(correlationId, pendingSnapshots, payload?.errorCode)

                    if (isLocal) {
                        notifyDMError(payload)
                    }
                }
            }

        const createAsyncDuplexer = (): Channel => {
            if (this.config.cedit || this.config.acceptTransactionsFromDuplexer) {
                const branchId = dsSiteApi.getBranchId()
                return this.server.createDuplexer(() => siteAPI.getInstance(), this.config.origin, logger, branchId)
            }
            return new MockCSDuplexer()
        }

        const onApprovedTx = (payload: Approved): void => {
            let {transactionId} = payload
            const {correlationId, transactionIds} = payload
            logger.interactionStarted('onApprovedTx', {})
            if (!transactionId && transactionIds) {
                transactionId = transactionIds[transactionIds.length - 1]
            }
            orderCheck.check(transactionId, correlationId, {usingCEdit: this.config.cedit})
            log.info(`approved ${correlationId}`)
            queue.add(getProcessApproved(payload))
            logger.interactionEnded('onApprovedTx', {})
        }

        const logRejection = (correlationId: string, reason?: string) => {
            const conflictDetected = 'conflict detected: '
            if (reason?.startsWith(conflictDetected)) {
                const json = reason?.substring(conflictDetected.length)
                const details = JSON.parse(json)
                log.info(`rejected ${correlationId} conflict detected: `, details)
            } else {
                log.info(`rejected ${correlationId} ${reason}`)
            }
        }

        const reportRejection = (payload: Rejected): void => {
            const {correlationId, reason} = payload
            logger.interactionStarted(CSAVE_TRANSACTION_REJECTED, {
                tags: {
                    isLocal: isLocalTransaction(correlationId)
                },
                extras: {
                    reason
                }
            })
            logRejection(correlationId, reason)
        }

        const onRejectedTx = (payload: Rejected): void => {
            const {correlationId} = payload
            reportRejection(payload)
            queue.add(getProcessRejected(correlationId, payload))
        }

        const loadTransactions = () => {
            queue.add(getProcessFullSync())
        }

        const groupTransactionsResults = (payload: BatchTransactions) => {
            const groupedResults: Record<string, BatchTransactionResult> = {}

            ;(payload.transactionsResults ?? []).forEach(({approved, rejected}) => {
                if (approved) {
                    const {correlationId, transactionId, transientData} = approved
                    if (transactionId !== null) {
                        if (!groupedResults[correlationId]) {
                            groupedResults[correlationId] = {
                                approved: {
                                    correlationId,
                                    transactionId,
                                    transientData
                                }
                            }
                        } else {
                            const approvedTx: Approved = groupedResults[correlationId].approved!
                            if (approvedTx.transactionIds) {
                                approvedTx.transactionIds.push(transactionId)
                            } else {
                                approvedTx.transactionIds = [approvedTx.transactionId!, transactionId]
                                approvedTx.transactionId = null
                            }
                        }
                    }
                } else if (rejected) {
                    const {correlationId, errorCode, reason} = rejected
                    if (!groupedResults[correlationId]) {
                        groupedResults[correlationId] = {
                            rejected: {
                                correlationId,
                                errorCode,
                                reason
                            }
                        }
                    }
                }
            })
            return Object.values(groupedResults)
        }

        async function reconnectToDuplexerAndSubscribeToChannel() {
            if (duplexerIsReconnecting) {
                return
            }
            logger.interactionStarted('reconnectToDuplexerAndSubscribeToChannel', {
                extras: {
                    branchId: dsSiteApi.getBranchId(),
                    channelName
                }
            })
            const oldDuplexerInstance = duplexer
            try {
                duplexerIsReconnecting = true
                if (duplexer) {
                    duplexer.removeChannelEventListeners()
                }
                duplexer = createAsyncDuplexer()
                await duplexer.subscribe(channelName)

                const approvedHandler = (payload: Approved) => {
                    logger.interactionStarted('gotApproveFromDuplexer', {
                        extras: {
                            correctionId: payload.correlationId,
                            transactionId: payload.transactionId ?? payload.transactionIds?.join(', '),
                            lastTransactionIdFromFacade: server.getLast()
                        }
                    })
                    if (server.getLast()) {
                        onApprovedTx(payload)
                    }
                    logger.interactionEnded('gotApproveFromDuplexer', {
                        extras: {correctionId: payload.correlationId}
                    })
                }
                const rejectHanlder = (payload: Rejected) => {
                    logger.interactionStarted('gotRejectFromDuplexer', {
                        extras: {
                            correctionId: payload.correlationId,
                            reason: payload.reason
                        }
                    })
                    onRejectedTx(payload)
                    logger.interactionEnded('gotRejectFromDuplexer', {
                        extras: {
                            correctionId: payload.correlationId
                        }
                    })
                }

                duplexer.on(ChannelEvents.batchTransactions, (payload: BatchTransactions) => {
                    const batchItems = Object.fromEntries(
                        payload.transactionsResults?.map(x => [
                            `${x.rejected?.correlationId ?? x.approved?.correlationId}`,
                            x.approved ? 'Approved' : 'Rejected'
                        ]) ?? []
                    )
                    logger.interactionStarted('batchTransactionsFromDuplexer', {
                        extras: {
                            correlationIds: batchItems,
                            lastTransactionIdFromFacade: server.getLast(),
                            branchId: dsSiteApi.getBranchId()
                        }
                    })

                    const transactions = groupTransactionsResults(payload)
                    transactions?.forEach(transactionResult => {
                        if (transactionResult.approved) {
                            approvedHandler(transactionResult.approved)
                        }
                        if (transactionResult.rejected) {
                            rejectHanlder(transactionResult.rejected)
                        }
                    })
                    logger.interactionEnded('batchTransactionsFromDuplexer', {
                        extras: {correlationIds: batchItems}
                    })
                })

                duplexer.on(ChannelEvents.approved, approvedHandler)
                duplexer.on(ChannelEvents.rejected, rejectHanlder)
                duplexer.on(ChannelEvents.majorSiteChange, (payload?: StaleEditorEnvironment) => {
                    if (payload?.reason === 'staleEditorEnvironment') {
                        log.info('majorSiteChange event received OUTDATED_VERSION')
                        hooks.onRefreshRequired?.('OUTDATED_VERSION')
                    } else {
                        log.info('majorSiteChange event received VERSION_RESTORED')
                        hooks.onRefreshRequired?.('VERSION_RESTORED')
                    }
                })
                duplexer.on(ChannelEvents.outOfSync, async () => {
                    logger.interactionStarted('processDuplexerOutOfSync', {
                        extras: {
                            lastTransactionIdFromFacade: server.getLast(),
                            branchId: dsSiteApi.getBranchId()
                        }
                    })
                    if (!config.cedit) {
                        logger.interactionEnded('processDuplexerOutOfSync', {
                            extra: {recover: false, reason: 'should not recover if cedit is false'}
                        })
                        return
                    }
                    log.info('Duplexer out of sync - loading transactions')
                    loadTransactions()
                    logger.interactionEnded('processDuplexerOutOfSync')
                })
                await server.onChannelReady?.()
            } catch (e: any) {
                log.error('error connecting to duplexer', e)
                logger.interactionStarted('subscribeToDuplexerFailed', {
                    extras: {error: JSON.stringify(e)}
                })
            } finally {
                duplexerIsReconnecting = false
                if (experimentInstance.isOpen('dm_closeOldDuplexerConnection')) {
                    oldDuplexerInstance?.close()
                }
            }
        }

        function handleRemovalError(error: SafeRemovalError) {
            const {exception, invalidData, namespace, dataType} = error
            logger.captureError(
                new ReportableError({
                    errorType: _.get(exception, ['errorType'], 'unknownSaveError'),
                    message: exception.message,
                    tags: {
                        additionalProperties: true,
                        namespace,
                        dataType,
                        saveFailure: 'handleRemovalError'
                    },
                    extras: {invalidData}
                })
            )
            eventEmitter.emit(CS_EVENTS.CSAVE.NON_RECOVERABLE_ERROR)
        }

        const logCSaveRejectWarning = (e: any) => {
            logger.captureError(
                new ReportableWarning(
                    getReportableFromError(e, {
                        errorType: 'cSaveRejectError',
                        message: e.message,
                        tags: {
                            status: e.extras?.status,
                            type: e.errorType
                        }
                    })
                )
            )
        }

        const didUserHavePermissionOnLoad = (permission: string) => {
            const permissionsOnLoad = _.get(window, ['documentServicesModel', 'permissionsInfo', 'permissions'], [])
            return permissionsOnLoad.includes(permission)
        }

        const shouldDalReject = (e: any) => {
            if (VersionInfoUpdateError.isVersionInfoUpdateError(e)) {
                logVersionInfoUpdateWarning(e, logger)
                return false
            }

            return !isNetworkError(e)
        }

        const handleCSaveError = (e: any, fromSnapshot: Null<SnapshotDal>, toSnapshot: SnapshotDal) => {
            if (e.status === 403 || e.extras?.status === 403) {
                logger.captureError(
                    new ReportableWarning({
                        errorType: 'csave-403',
                        message: 'USER_NOT_AUTHORIZED_FOR_SITE (403 error on csave)',
                        tags: {
                            savePermissionOnLoad: didUserHavePermissionOnLoad('html-editor.save'),
                            editPermissionOnSave: didUserHavePermissionOnLoad('html-editor.edit')
                        },
                        extras: {
                            originalError: JSON.stringify(e)
                        }
                    })
                )
            }

            const userInfo = dal.get(pointers.documentServicesModel.getUserInfo())
            if (shouldDalReject(e)) {
                // Revert the snapshot in the dal and report
                dal.reject(toSnapshot.id)
                logCSaveRejectWarning(e)
            } else {
                // Remove the CSAVE_TAG from the latest CSAVE snapshot, without removing the snapshot itself from the dal
                // This means that this snapshot will be sent with the *next* csave
                snapshots.removeLastSnapshot(CSAVE_TAG)
            }

            this.hooks.onDiffSaveFinished?.(convertToDSError(e, userInfo))

            log.error('Error while trying to save continuously', e)
        }

        interface RetryServerSaveTaskOptions {
            getReasonForRetry?(e: any): string | undefined
        }

        const retryServerSaveTask = async <T>(
            name: string,
            fn: () => Promise<T>,
            options?: RetryServerSaveTaskOptions
        ): Promise<T> =>
            retryTaskAndReport({
                task: fn,
                checkIfShouldRetry: (e: any) => {
                    let reason
                    if (e?.status === 503) {
                        reason = '503'
                    } else if (
                        _.get(e, ['details', 'applicationError', 'code']) ===
                        serverSaveErrorCodes.IDENTITY_UNKNOWN_RUNTIME_ERROR
                    ) {
                        reason = serverSaveErrorCodes.IDENTITY_UNKNOWN_RUNTIME_ERROR
                    } else {
                        reason = options?.getReasonForRetry?.(e)
                    }
                    if (reason) {
                        return {shouldRetry: true, reason}
                    }
                    return {shouldRetry: false}
                },
                interactionName: name,
                maxRetries: 1,
                logger
            })

        const saveSync = async (payload: CreateTransactionRequest, correlationId: string) => {
            const {transactionId} = await retryServerSaveTask('syncSave', () => server.save(payload))
            reportTransactionId(logger, log, transactionId, correlationId, server.getLast())
            processLocalApproved({correlationId, transactionId: transactionId!})
            updateLastTxId(transactionId!)
        }

        const saveSyncWithMultipleTransactions = async (payload: SaveRequest) => {
            const response = await retryServerSaveTask('saveSyncWithMultipleTransactions', () =>
                server.saveSyncWithMultipleTransactions(payload)
            )
            const rejectedTransactions = _.filter(response.results, 'rejected')
            const approvedTransactions = _.filter(response.results, 'approved')
            _.forEach(approvedTransactions, (transaction: TransactionResult) => {
                const {correlationId, transactionId} = transaction.approved!
                reportTransactionId(logger, log, transactionId, correlationId, server.getLast())
                processLocalApproved({correlationId: correlationId!, transactionId: transactionId!})
                updateLastTxId(transactionId!)
            })
            _.forEach(rejectedTransactions, transaction => {
                const {correlationId} = transaction.rejected!
                const fn = getProcessRejected(correlationId!, transaction.rejected!)
                fn()
            })
            return response
        }
        const shouldNotSquashTransactions = () =>
            _.isBoolean(this.config.shouldSquashTransactions) && !this.config.shouldSquashTransactions

        const createAtomicPayload = (payload: SaveRequest, transactionBatch: PendingTransaction[]): SaveRequest => {
            const groupedTransactions = _.groupBy(transactionBatch, 'correlationId')
            Object.values(groupedTransactions).forEach(transactions => {
                if (transactions.length >= THRESHOLD_OF_FRAGMENTS_TO_COMMIT) {
                    const limitError = new FragmentThresholdWarning(THRESHOLD_OF_FRAGMENTS_TO_COMMIT)
                    logger.captureError(limitError)
                }
            })
            const atomicTransactions = _.flatMap(
                groupedTransactions,
                transactions =>
                    (transactions.length > 1
                        ? [{transactionGroup: {transactions}}]
                        : [{transaction: transactions[0]}]) as AtomicTransaction[]
            )

            return {
                ...payload,
                atomicTransactions
            }
        }

        const splitAtomicTransactionsByLimit = ({atomicTransactions}: SaveRequest): PendingTransaction[][] => {
            if (!atomicTransactions) {
                throw new Error('atomicTransactions is missing')
            }
            return atomicTransactions.reduce<PendingTransaction[][]>((result, atomicTransaction) => {
                const lastBatch = _.last(result)
                const transactions = getPayloadTransactions({atomicTransactions: [atomicTransaction]})

                if (lastBatch && lastBatch.length + transactions.length <= this.config.csaveTransactionsLimit) {
                    lastBatch.push(...transactions)
                } else {
                    result.push(transactions)
                }

                return result
            }, [])
        }

        const splitSaveByTransactionsLimit = async (payload: SaveRequest) => {
            const splitTransactions = splitAtomicTransactionsByLimit(payload)
            log.info(
                `splitting save request by transactions limit of ${this.config.csaveTransactionsLimit} to ${splitTransactions.length} chunks`
            )
            for (const transactionBatch of splitTransactions) {
                const splitPayload = createAtomicPayload(payload, transactionBatch)
                await retryServerSaveTask('asyncSave', () => server.asyncSave(splitPayload))
            }
        }

        const saveSyncFragments = async (payload: SaveRequest) => {
            const transactionsToProcess = payload.atomicTransactions
            if (!transactionsToProcess) return

            await Promise.all(
                transactionsToProcess.map(async transaction => {
                    let correlationId: string | undefined

                    if ('transaction' in transaction) {
                        correlationId = transaction.transaction?.correlationId
                    } else if ('transactionGroup' in transaction) {
                        correlationId = transaction.transactionGroup?.transactions?.[0]?.correlationId
                    }

                    if (!correlationId) {
                        throw new Error('Missing correlationId in transaction')
                    }

                    const payloadToSave: CreateTransactionRequest = {
                        atomicTransaction: transaction as AtomicTransaction,
                        branchId: payload.branchId
                    }

                    return saveSync(payloadToSave, correlationId)
                })
            )
        }

        // TODO: DM-13712 remove this once the issue is fixed
        const validateActionsInPayload = (payload: SaveRequest) => {
            const invalidActions: Action[] = getPayloadTransactions(payload)
                .flatMap(transaction => transaction.actions ?? [])
                .filter(action => action.op === ActionOperation.REMOVE && !!action?.value)

            if (invalidActions.length > 0) {
                logger.interactionStarted('invalidActionsInSavePayload', {
                    extras: {
                        invalidActions: invalidActions.map(action => ({
                            itemId: action.id,
                            value: JSON.stringify(action.value)?.slice(0, 100)
                        }))
                    },
                    tags: {opType: ActionOperation.REMOVE}
                })
            }
        }

        const sendSaveToServer = async (payload: SaveRequest) => {
            if (this.config.cedit) {
                return await splitSaveByTransactionsLimit(payload)
            } else if (shouldNotSquashTransactions()) {
                return await saveSyncWithMultipleTransactions(payload)
            }
            validateActionsInPayload(payload)
            return await saveSyncFragments(payload)
        }

        const saveDiff = async (payload: SaveRequest, toSnapshot: SnapshotDal) => {
            const correlationId = toSnapshot.id
            if (!this.firstCorrelationId) {
                this.firstCorrelationId = correlationId
            }
            this.hooks.onDiffSaveStarted?.()

            snapshots.tagSnapshot(CSAVE_TAG, toSnapshot)
            if (this.isSaveDisabledDuringRequiredAndPrimary) {
                logger.captureError(
                    new ReportableError({
                        message: 'saveDiff',
                        errorType: 'cSaveDuringFullSave',
                        extras: {
                            pendingIndex: this.pendingIndex,
                            correlationId
                        }
                    })
                )
            }

            startCSaveTransactionInteraction(correlationId)
            notifier.resetTimer(correlationId, {
                timeout: cSaveNotifierSendToServerTimeoutLimit
            })
            const res = await sendSaveToServer(payload)
            this.hooks.onDiffSaveFinished?.()
            return res
        }

        const createFragmentTransactions = (toSnapshot: SnapshotDal): PendingTransaction[] => {
            const siteVersion = `${dsSiteApi.getSiteVersion()}`
            const {atomicResult, error} = converter.convertFragmentsData(
                toSnapshot.getPreviousSnapshot()!,
                toSnapshot,
                logger,
                experimentInstance,
                extensionAPI
            )
            if (error) {
                throw error
            }
            if (!atomicResult) {
                throw new Error('No atomicResult')
            }
            if (_.isEmpty(atomicResult)) {
                return [
                    {
                        correlationId: toSnapshot.id,
                        actions: [] as Action[],
                        metadata: {siteVersion, initiator: {initiatorType: InitiatorType.DM_CLIENT}},
                        envSessionId: dsSiteApi.getEnvSessionId()
                    }
                ]
            }
            return _.map(atomicResult, diff => ({
                correlationId: toSnapshot.id,
                actions: diff! as Action[],
                metadata: {siteVersion, initiator: {initiatorType: InitiatorType.DM_CLIENT}},
                envSessionId: dsSiteApi.getEnvSessionId()
            }))
        }

        const addEmptyTransactionToSnapshotMap = (emptyTx: PendingTransaction, allTxs: PendingTransaction[]) => {
            const txIndex = allTxs.indexOf(emptyTx)
            const nextTransaction = allTxs.slice(txIndex, allTxs.length).find(tx => tx.actions?.length !== 0)

            let previousTransaction
            if (!nextTransaction) {
                previousTransaction = allTxs
                    .slice(0, txIndex)
                    .reverse()
                    .find((tx: PendingTransaction) => tx.actions?.length !== 0)
            }

            const transactionKey = nextTransaction?.correlationId || previousTransaction?.correlationId
            if (!transactionKey) {
                return
            }

            this.emptySnapshotsMap.set(transactionKey, emptyTx.correlationId!)
        }

        const filterOutEmptyTransactions = (transactions: PendingTransaction[]): PendingTransaction[] => {
            return transactions.filter(edsTx => {
                if (edsTx.actions?.length === 0) {
                    addEmptyTransactionToSnapshotMap(edsTx, transactions)
                    return false
                }
                return true
            })
        }

        const createTransactionsCEdit = (
            fromSnapshot: Null<SnapshotDal>,
            toSnapshot: SnapshotDal
        ): CreateTransactionReq => {
            const chain = createSnapshotChain(fromSnapshot, toSnapshot, 'createTransactionsCEdit')
            try {
                const branchId = dsSiteApi.getBranchId()
                const transactions: PendingTransaction[] = filterOutEmptyTransactions(
                    _.flatten(chain.map(createFragmentTransactions))
                )
                const payload = createAtomicPayload({branchId}, transactions)
                return {payload}
            } catch (e) {
                return {error: e as SafeRemovalError}
            }
        }

        const createTransactions = (fromSnapshot: Null<SnapshotDal>, toSnapshot: SnapshotDal): CreateTransactionReq => {
            const {continuousSavePayloadBuilder} = extensionAPI as ContinuousSavePayloadBuilderExtensionAPI

            if (this.config.cedit || shouldNotSquashTransactions()) {
                return createTransactionsCEdit(fromSnapshot, toSnapshot)
            }

            return continuousSavePayloadBuilder.createTransactionsCSave(fromSnapshot, toSnapshot, this.server.getLast())
        }

        const getToSnapshotWithinActionsLimit = (
            fromSnapshot: Null<SnapshotDal>,
            toSnapshot: SnapshotDal
        ): SnapshotDal => {
            const getSnapshotActionsCount = (snapshot: SnapshotDal) => snapshot.getStore().size()
            const snapshotsChain = createSnapshotChain(fromSnapshot, toSnapshot, 'getSnapshotActionsCount')

            let actionsSum = 0
            const finalSnapshot = _.reduce(
                snapshotsChain,
                (lastValidSnapshot, candidateSnapshot) => {
                    actionsSum += getSnapshotActionsCount(candidateSnapshot)
                    if (actionsSum > this.config.csaveSnapshotActionsLimit) {
                        return lastValidSnapshot
                    }
                    return candidateSnapshot
                },
                snapshotsChain[0]
            )

            return finalSnapshot
        }

        const resetPendingIndexForFirstChunk = (isSavingChunk: boolean) => {
            if (!isSavingChunk) {
                this.pendingIndex = -1
            }
        }

        const shouldReportBigActionData = (action: Action) => {
            return action.namespace === 'tpaSharedState' && action?.value?.data && !action.id?.includes('THEME_CHANGE')
        }
        const getTransactionsContent = (payload: SaveRequest) => {
            const sharedStateActions: any[] = []
            const transactions_content = _.map(getPayloadTransactions(payload), ({correlationId, actions = []}) => {
                actions.forEach(action => {
                    if (shouldReportBigActionData(action)) {
                        const size = JSON.stringify(action.value.data).length
                        if (size > 1024) {
                            sharedStateActions.push({
                                id: action.id,
                                size
                            })
                        }
                    }
                })
                return {
                    correlationId,
                    content: _.countBy(actions, 'namespace')
                }
            })
            return {transactions_content, sharedStateActions}
        }

        const getActionCountByCorrelationIdForNonCedit = (payload: SaveRequest, correlationId?: string) => {
            const result: Record<string, number> = {}
            if (correlationId) {
                result[correlationId] = _.sum(
                    getPayloadTransactions(payload).map(transaction => transaction.actions?.length ?? 0)
                )
            }
            return result
        }

        const getActionCountByCorrelationIdForCedit = (payload: SaveRequest) => {
            const result: Record<string, number> = {}
            const transactions =
                getPayloadTransactions(payload).filter(
                    transaction => !!transaction.correlationId && !!transaction.actions
                ) ?? []
            for (const transaction of transactions) {
                result[transaction.correlationId!] =
                    (result[transaction.correlationId!] ?? 0) + transaction.actions!.length
            }

            return result
        }

        const updateActionCountByCorrelationId = (payload: SaveRequest, correlationId: string) => {
            const actionCountByCorrelationId = this.config.cedit
                ? getActionCountByCorrelationIdForCedit(payload)
                : getActionCountByCorrelationIdForNonCedit(payload, correlationId)

            this.actionCountByCorrelationId = Object.assign(this.actionCountByCorrelationId, actionCountByCorrelationId)
        }

        const logCsavePayloadSize = (payload: SaveRequest) => {
            const actionCount = _.sum(getPayloadTransactions(payload).map(t => t.actions?.length))

            logger.interactionStarted(CSAVE_PAYLOAD_SIZE, {
                extras: {
                    actionCountSent: actionCount,
                    unappliedActionCountSinceLastRevision: this.unappliedActionCountSinceLastRevision,
                    unappliedTransactionCountSinceLastRevision: this.unappliedTransactionCountSinceLastRevision
                }
            })
            logger.interactionEnded(CSAVE_PAYLOAD_SIZE)
        }

        const saveSnapshotsDiff = async (
            fromSnapshot: Null<SnapshotDal>,
            toSnapshot: SnapshotDal,
            isSavingChunk: boolean = false
        ): Promise<void | SaveTransactionResponse> => {
            if (toSnapshot === fromSnapshot) {
                log.info('trying to save diff between same snapshots')
                notifier.resolve(toSnapshot.id)
                return
            }
            logger.interactionStarted(CSAVE_INTERACTION, {
                stack: true,
                extras: {
                    correlation_id: toSnapshot.id,
                    fromSnapshot: fromSnapshot?.id,
                    branchId: dsSiteApi.getBranchId()
                }
            })
            resetPendingIndexForFirstChunk(isSavingChunk)
            const snapshotToSave = getToSnapshotWithinActionsLimit(fromSnapshot, toSnapshot)
            const isLastChunk = snapshotToSave.id === toSnapshot.id

            const {error, payload} = createTransactions(fromSnapshot, snapshotToSave)

            if (error) {
                handleRemovalError(error)
                snapshots.tagSnapshot(CSAVE_TAG, snapshots.getLastSnapshot()!)
                this.pendingIndex = -1
                notifier.resolve(snapshotToSave.id)
                if (experimentInstance.isOpen('dm_rejectDalOnBuildingPayloadForCsaveError')) {
                    dal.reject(snapshotToSave.id)
                }
                return
            }
            if (_.isEmpty(getPayloadTransactions(payload ?? {}))) {
                log.info('no diff, skipping save')
                if (!this.config.cedit) {
                    processLocalApproved({correlationId: snapshotToSave.id, transactionId: null})
                    snapshots.tagSnapshot(CSAVE_TAG, snapshotToSave)
                }
                notifier.resolve(snapshotToSave.id)
                logger.interactionEnded(CSAVE_INTERACTION, {
                    tags: {skipped: true},
                    extras: {correlation_id: snapshotToSave.id, fromSnapshot: fromSnapshot?.id}
                })
                await handleTheRestOfTheChunksAndThePendingSnapshots(snapshotToSave, toSnapshot, isLastChunk)
                return
            }
            let res
            try {
                logCsavePayloadSize(payload!)
                updateActionCountByCorrelationId(payload!, snapshotToSave.id)
                res = await saveDiff(payload!, snapshotToSave)
            } catch (e: any) {
                const shouldThrowVersionError = VersionInfoUpdateError.shouldThrowVersionInfoUpdateError(
                    e,
                    logger,
                    payload
                )
                throw shouldThrowVersionError ? new VersionInfoUpdateError(e) : e
            }

            await handleTheRestOfTheChunksAndThePendingSnapshots(snapshotToSave, toSnapshot, isLastChunk)

            const extras = {correlation_id: toSnapshot.id, fromSnapshot: fromSnapshot?.id} as any
            const {sharedStateActions} = getTransactionsContent(payload!)

            if (sharedStateActions.length > 0) {
                extras.sharedStateActions = sharedStateActions
            }
            logger.interactionEnded(CSAVE_INTERACTION, {
                tags: {skipped: false},
                extras
            })
            return res
        }

        const handlePendingSnapshots = async () => {
            if (this.pendingIndex > -1) {
                const fromLastCsaveSnapshot = getLastCsaveSnapshot(snapshots)
                const toPendingSnapshot = snapshots.getSnapshotByTagAndIndex(CSAVE_PENDING_TAG, this.pendingIndex)
                await saveSnapshotsDiff(fromLastCsaveSnapshot, toPendingSnapshot)
            }
        }

        async function handleTheRestOfTheChunksAndThePendingSnapshots(
            fromChunkSnapshot: SnapshotDal,
            toSnapshot: SnapshotDal,
            isLastChunk: boolean
        ) {
            if (!isLastChunk) {
                await saveSnapshotsDiff(fromChunkSnapshot, toSnapshot, true)
            } else {
                await handlePendingSnapshots()
            }
        }

        const setSaving = (isSaving: boolean) => {
            log.info('state.saving', isSaving)

            if (this.saving && !isSaving && !this.isSaveDisabledDuringRequiredAndPrimary) {
                this.saving = isSaving
                eventEmitter.emit(CS_EVENTS.SAVE.FINISHED_SAVING)
                return
            }

            this.saving = isSaving
        }

        const disableSaveDuringRequiredAndPrimary = (shouldDisableSave: boolean) => {
            if (this.isSaveDisabledDuringRequiredAndPrimary && !shouldDisableSave && !this.saving) {
                this.isSaveDisabledDuringRequiredAndPrimary = shouldDisableSave
                eventEmitter.emit(CS_EVENTS.SAVE.FINISHED_SAVING)
                return
            }

            this.isSaveDisabledDuringRequiredAndPrimary = shouldDisableSave
        }

        const createRevision = async (
            args: CreateRevArgs,
            updateSiteDto: Partial<UpdateSiteDTO>
        ): Promise<CreateRevisionResponse> => {
            const editorSessionId = dsSiteApi.getEditorSessionId()

            const req: CreateRevisionRequest = {
                dsOrigin: args.dsOrigin,
                initiatorOrigin: args.initiatorOrigin,
                editorVersion: args.editorVersion,
                initiator: args.initiator,
                viewerName: args.viewerName,
                editorSessionId,
                metaSiteActions: updateSiteDto.metaSiteActions,
                branchId: updateSiteDto.branchId,
                siteName: updateSiteDto.metaSiteData?.siteName,
                closedWixCodeAppId: updateSiteDto?.wixCodeAppData?.codeAppId,
                lastTransactionId: args.suppressUnappliedTransactions
                    ? undefined
                    : args.lastTransactionId ?? server.getLast(),
                disableAsyncGc: !this.config.cedit,
                suppressUnappliedTransactions: args.suppressUnappliedTransactions,
                appsStateDelta: updateSiteDto.appsStateDelta
            }
            logger.interactionStarted('createRevisionData', {
                extras: {
                    req
                }
            })
            const serverResponse: ServerCreateRevisionResponse = await retryServerSaveTask(
                'createRevision',
                () => server.createRevision(req),
                {
                    getReasonForRetry: (e: any) => {
                        if (_.get(e, ['details', 'applicationError', 'code']) === saveErrors.CLONE_GRID_APP_FAILED) {
                            return saveErrors.CLONE_GRID_APP_FAILED
                        }
                    }
                }
            )
            server.setLastSaveDate(new Date())
            log.info('createRevision called, result: ', serverResponse)
            logger.interactionEnded('createRevisionData', {
                extras: {
                    siteRevision: serverResponse?.siteRevision
                }
            })
            const response: CreateRevisionResponse = convertToClientCreateRevisionResponse(serverResponse)
            dsSiteApi.setSiteRevision(response.siteRevision.revision)
            dsSiteApi.setSiteVersion(response.siteRevision.version)
            return response
        }

        const createRevisionWithSuppressTransactions = async (): Promise<void> => {
            logger.interactionStarted('sendCreateRevisionWithSuppress', {
                extras: {
                    lastTransactionId: server.getLast(),
                    lastTransactionIdOnServer: server.getLastUpdatedOnServer()
                }
            })
            try {
                const response: CreateRevisionResponse = await createRevision(
                    {
                        dsOrigin: config.origin,
                        initiatorOrigin: 'autosaveRestoreCsave',
                        editorVersion: `${dsSiteApi.getSiteVersion()}`,
                        initiator: 'autosaveRestoreCsave',
                        viewerName: 'tb',
                        suppressUnappliedTransactions: true
                    },
                    {
                        branchId: dsSiteApi.getBranchId(),
                        metaSiteData: {siteName: dal.get(pointers.documentServicesModel.getSiteName())}
                    }
                )
                logger.interactionEnded('sendCreateRevisionWithSuppress', {
                    extras: {
                        processedTransactions: response?.processedTransactions,
                        siteRevision: response?.siteRevision
                    }
                })
                if (response?.processedTransactions?.lastTransactionId) {
                    server.setLast(response?.processedTransactions?.lastTransactionId)
                }
                distributor.distributeMessage<TransactionsDroppedMessage>('transactionsDropped', {})
            } catch (e) {
                const error = makeReportable(e)
                logger.captureError(error)
                log.error('Error while sending create revision with suppress', error)
            }
        }

        const sendCreateRevisionWithSuppressIfNeeded = synchronized(async () => {
            if (config.autosaveRestore === 'false' && server.getLast() !== server.getLastUpdatedOnServer()) {
                await createRevisionWithSuppressTransactions()
            }
        })

        const saveFromLastCSaveSnapshot = async (snapshot: SnapshotDal): Promise<void | SaveTransactionResponse> => {
            await sendCreateRevisionWithSuppressIfNeeded()
            if (this.isSaveDisabledDuringRequiredAndPrimary && !this.saving) {
                logger.captureError(
                    new ReportableError({
                        message: `saveFromLastCSaveSnapshot`,
                        errorType: 'cSaveDuringFullSave',
                        extras: {
                            pendingIndex: this.pendingIndex
                        }
                    })
                )
            }

            if (this.saving || this.isSaveDisabledDuringRequiredAndPrimary) {
                log.info('save is in progress, skipping save')
                logger.interactionEnded('SAVE_SKIPPED', {
                    extras: {
                        saving: this.saving,
                        isSaveDisabledDuringRequiredAndPrimary: this.isSaveDisabledDuringRequiredAndPrimary
                    }
                })
                this.pendingIndex = snapshots.tagSnapshot(CSAVE_PENDING_TAG, snapshot)
            } else {
                const csaveSnap = getLastCsaveSnapshot(snapshots)
                try {
                    setSaving(true)
                    return await saveSnapshotsDiff(csaveSnap, snapshot)
                } catch (e) {
                    const recentCsaveSnapshot = getLastCsaveSnapshot(snapshots)
                    if (recentCsaveSnapshot?.id !== undefined) {
                        notifier.reject(recentCsaveSnapshot.id, e)
                    }

                    const error = makeReportable(e)
                    if (isStaleTransactionError(error)) {
                        const correlationId = _.get(error, ['extras', 'latestPersistedTransaction', 'correlationId'])
                        const isLocalCorrelationId = !!dal._snapshots.findById(correlationId)
                        if (isLocalCorrelationId) {
                            error.errorType = saveErrors.SITE_STALE_STATE_LOCAL_CHANGE
                        }
                    }
                    handleCSaveError(e, csaveSnap, recentCsaveSnapshot)
                    throw error
                } finally {
                    setSaving(false)
                }
            }
        }

        const save = async () => {
            if (shouldSave()) {
                const lastSnapshot = snapshots.getLastSnapshot()!
                if (lastSnapshot === undefined) {
                    logger.captureError(
                        new ReportableWarning({
                            errorType: 'NoSnapshot',
                            message: 'No snapshot to save from',
                            tags: {
                                csaveOp: 'save'
                            }
                        })
                    )
                }
                const lastCSave = getLastCsaveSnapshot(snapshots)
                if (lastSnapshot === lastCSave) {
                    logger.interactionEnded('SAVE_SNAPSHOT_SAME_AS_LAST_CSAVE')
                    return
                }

                notifier
                    .register(lastSnapshot.id, {
                        extras: {
                            cedit: this.config.cedit,
                            isDuplexerDisconnected: !duplexer?.connected
                        },
                        rejectionOnTimeoutCb: (error: any) => {
                            const userInfo = dal.get(pointers.documentServicesModel.getUserInfo())
                            this.hooks.onDiffSaveFinished?.(convertToDSError(error, userInfo))
                        }
                    })
                    .catch(_.noop)

                try {
                    return await saveFromLastCSaveSnapshot(lastSnapshot)
                } catch (e) {
                    logger.captureError(e as Error, {tags: {csaveOp: 'save'}})
                    notifier.reject(lastSnapshot.id, e)
                    throw e
                }
            } else {
                const isSavePermittedValue = isSavePermitted()
                const shouldAutoSaveValue = shouldAutoSaveFromDsSiteApi()
                const isDraftValue = dsSiteApi.getIsDraft()
                const isCSaveEnabledValue = isCSaveEnabled()
                logger.interactionEnded('SAVE_DISABLED', {
                    extras: {
                        isSavePermitted: isSavePermittedValue,
                        isCSaveEnabled: isCSaveEnabledValue,
                        shouldAutoSave: shouldAutoSaveValue,
                        isDraft: isDraftValue,
                        disableSave: this.config.disableSave,
                        disableAutoSave: this.config.disableAutoSave,
                        isEnabled: this.isEnabled
                    }
                })
            }
        }

        const createDummyTx = () => {
            const somePointerOnMasterPage = {id: 'masterPage', type: 'fixerVersions'}
            dal.touch(somePointerOnMasterPage)
            dal.commitTransaction()
        }

        const saveAndWait = async (triggeredFromForcedSave: boolean): Promise<void> => {
            if (!this.isInitialized) {
                logger.captureError(
                    new ReportableWarning({
                        errorType: 'NotInitialized',
                        message: 'Save and wait called before initialization',
                        tags: {
                            csaveOp: 'saveAndWait',
                            triggeredFromForcedSave
                        }
                    })
                )
            }
            let snapshot = snapshots.getLastSnapshot()!
            if (snapshot === undefined) {
                logger.captureError(
                    new ReportableWarning({
                        errorType: 'NoSnapshot',
                        message: 'No snapshot to save from',
                        tags: {
                            csaveOp: 'saveAndWait',
                            triggeredFromForcedSave
                        }
                    })
                )
            }
            const lastCSave = getLastCsaveSnapshot(snapshots)
            if (snapshot === lastCSave) {
                return await notifier.waitForCurrentPending()
            }
            if (experimentInstance.isOpen('dm_addDummyTxToSaveAndWait')) {
                //adding a meaningless non empty transaction so there will be definitely a csave sent to server.
                //we need it because we want to be sure that when this dummy tx will be approved the csaves that were sent beforehand are already approved/rejected by the server
                createDummyTx()
                snapshot = snapshots.getLastSnapshot()
            }
            if (
                experimentInstance.isOpen('dm_skipSaveOnAlreadyRegisteredNotifier') &&
                notifier.isRegistered(snapshot.id)
            ) {
                logger.interactionStarted('saveAndWait_skippedAlreadyRegistered', {
                    extras: {snapshotId: snapshot.id, triggeredFromForcedSave}
                })
                return notifier.getRegisteredPromise(snapshot.id)!
            }
            const promise = notifier.register(snapshot.id, {
                extras: {
                    cedit: this.config.cedit,
                    isDuplexerDisconnected: !duplexer?.connected
                }
            })
            try {
                // if saving, will wait on promise until next save
                await saveFromLastCSaveSnapshot(snapshot)
                // it's possible using stack here is not optimal
                // however, I dont even think we have an end for every start (in case of duplexer approval)
                // so for now, we will use stack - and if we have close to 100% success, then we can discuss optimizing actual duration
                logger.interactionStarted('saveAndWaitWaitingForResponse', {stack: true})
                // in case of success the promise is resolved by the duplexer approval
            } catch (e) {
                logger.captureError(makeReportable(e), {tags: {csaveOp: 'saveAndWait', triggeredFromForcedSave}})
                log.error('Error saving from last csave snapshot', e as Error)
                notifier.reject(snapshot.id, e)
            }

            return promise
        }

        const saveAndWaitForResult = async (): Promise<void> => {
            const triggeredFromForcedSave = false
            if (shouldSave()) {
                await saveAndWait(triggeredFromForcedSave)
            }
        }

        const forceSaveAndWaitForResult = async (): Promise<void> => {
            const triggeredFromForcedSave = true
            if (isSavePermitted()) {
                logger.interactionStarted('forceSaveAndWaitForResult', {stack: true})
                await saveAndWait(triggeredFromForcedSave)
                logger.interactionEnded('saveAndWaitWaitingForResponse')
                logger.interactionEnded('forceSaveAndWaitForResult')
            }
        }

        const mergeActions = (actionsToApply: Action[]): void => {
            const changes: Store = actionsToDalChanges(actionsToApply)
            if (changes.isEmpty()) {
                ;(changes as FragmentStore).setFragmentStore(PAGE_FRAGMENT_TYPE, createStore())
            }
            const autosaveInfoPointer = pointers.general.getAutosaveInfo()
            const autoSaveInfo = _.cloneDeep(dal.get(autosaveInfoPointer)) ?? {}
            autoSaveInfo.changesApplied = true
            const pageChanges = (changes as FragmentStore).getFragmentStore(PAGE_FRAGMENT_TYPE)
            pageChanges!.set(autosaveInfoPointer, autoSaveInfo)
            dal.mergeToApprovedStore(changes, 'get-transactions')
        }

        const validate = (actionsToApply: Action[]) => {
            try {
                const store: Store = actionsToDalChanges(actionsToApply)
                const transactionStore = store.asJson()
                const dalStore = dal._getAllFragmentsApprovedStoreAsJson?.()
                logger.interactionStarted(CSAVE_VALIDATION_INTERACTION)
                validateCSaveTransaction(
                    dal,
                    dalStore!,
                    transactionStore,
                    (extensionAPI as SchemaExtensionAPI).schemaAPI,
                    logger,
                    experimentInstance
                )
                logger.interactionEnded(CSAVE_VALIDATION_INTERACTION)
            } catch (e) {
                logger.captureError(e as Error, tagsFromError(e as Error))
                return false
            }
            return true
        }

        const initChannel = async () => {
            if (!duplexer) {
                await reconnectToDuplexerAndSubscribeToChannel()
            }
        }

        const migrateActions = (actions: Action[]): Action[] => {
            let shouldReportBI = false

            for (const action of actions) {
                const id = getIdFromAction(action)
                shouldReportBI = shouldReportBI || action.id !== id
                action.id = id
            }

            if (shouldReportBI) {
                logger.interactionStarted('OldMLKeysInTransaction', {
                    extras: {
                        message:
                            'Transaction contains old ML keys of the form pageId^langCode^dataItem-id instead of langCode^dataItem-id'
                    }
                })
            }

            return actions
        }

        const loadAdditionalPages = async (pages: string[]) => {
            const lastTransactionId = dsSiteApi.getAutosaveInfo()?.lastTransactionId ?? '0'
            const branchId = dsSiteApi.getBranchId()

            const transactionResult = await getStoreWithCache(
                branchId,
                lastTransactionId,
                this.config.untilTransactionId,
                getFragmentTypesFilter()
            )
            if (!hasTransactionsSinceRevision(transactionResult)) {
                return false
            }
            const partialPagesSet = new Set(pages)
            transactionResult.actions = transactionResult.actions?.filter(action =>
                partialPagesSet.has(action?.value?.metaData?.pageId)
            )
            const changes = actionsToDalChanges(migrateActions(removeFromLoadStore(transactionResult.actions!)))
            dal.mergeToApprovedStore(changes, 'load-additional-partial-pages')
            return true
        }

        const initFragmentsTransactions = async () => {
            logger.interactionStarted('initFragmentsTransactions')
            const lastTransactionId = dsSiteApi.getAutosaveInfo()?.lastTransactionId ?? '0'
            const branchId = dsSiteApi.getBranchId()
            const transactionResult = await getStoreWithCache(
                branchId,
                lastTransactionId,
                this.config.untilTransactionId,
                getFragmentTypesFilter()
            )
            if (!hasTransactionsSinceRevision(transactionResult)) {
                logger.interactionEnded('initFragmentsTransactions')
                return
            }
            transactionResult.actions = transactionResult.actions?.filter(
                action => action.fragmentType !== PAGE_FRAGMENT_TYPE
            )

            if (transactionResult.actions?.length) {
                const changes: Store = actionsToDalChanges(transactionResult.actions)
                dal.mergeToApprovedStore(changes, 'load-fragments-transactions')
            }
            logger.interactionEnded('initFragmentsTransactions', {
                extras: {
                    actionsCount: transactionResult.actions?.length
                }
            })
        }

        const initCSaveInternal = async (partialPages: string[]) => {
            const lastTransactionId = dsSiteApi.getAutosaveInfo()?.lastTransactionId ?? '0'
            server.setLast(lastTransactionId)
            log.info(`lastTransactionId=${lastTransactionId}`)
            const branchId = dsSiteApi.getBranchId()

            const transactionResult = await getStoreWithCache(
                branchId,
                lastTransactionId,
                this.config.untilTransactionId,
                getFragmentTypesFilter()
            )
            if (!hasTransactionsSinceRevision(transactionResult)) {
                return false
            } else if (this.config.autosaveRestore === 'false') {
                logger.interactionStarted('initCSaveInternal-skip', {
                    extras: {
                        reason: 'autosave-restore-disabled',
                        lastTransactionId: transactionResult.lastTransactionId
                    }
                })
                server.setLastUpdatedOnServer(transactionResult.lastTransactionId!)
                return false
            }
            const partialPagesSet = new Set(partialPages)
            transactionResult.actions =
                partialPages.length > 0
                    ? transactionResult.actions?.filter(
                          action =>
                              action.fragmentType === PAGE_FRAGMENT_TYPE &&
                              (!action?.value?.metaData?.pageId || partialPagesSet.has(action?.value?.metaData?.pageId))
                      )
                    : transactionResult.actions?.filter(action => action.fragmentType === PAGE_FRAGMENT_TYPE)
            server.setLast(transactionResult.lastTransactionId!)
            server.setLastSaveDate(transactionResult.lastTransactionDateCreated!)
            initUnappliedTransactions(transactionResult)
            const actionsToApply = migrateActions(removeFromLoadStore(transactionResult.actions!))

            if (!this.config.disableCSaveValidationOnInitialization) {
                if (experimentInstance.isOpen('dm_removeDisableCSaveValidationOnInitialization')) {
                    //remove this after exp merge, this is just for the logs that will help to investigate corrupted transactions
                    validate(actionsToApply)
                } else {
                    const valid = validate(actionsToApply)
                    if (!valid) {
                        return false
                    }
                }
            }

            setUnappliedActionCountSinceLastRevision(transactionResult.originalActionsCount)
            setUnappliedTransactionCountSinceLastRevision(transactionResult.transactionCount)
            snapshots.takeSnapshot(SNAPSHOTS.BEFORE_AUTOSAVE_APPLY)
            snapshots.takeSnapshot(SNAPSHOTS.MOBILE_MERGE)
            logger.interactionStarted('initCSaveInternal-mergeActions', {
                extras: {actionsCount: actionsToApply.length}
            })
            mergeActions(actionsToApply)
            logger.interactionEnded('initCSaveInternal-mergeActions', {
                extras: {actionsCount: actionsToApply.length}
            })
            dsSiteApi.setActionsCount(actionCountToAutosaveActionCount(actionsToApply.length))
            return true
        }

        const initCSave = async (partialPages: string[] = []) => {
            if (this.isInitialized) {
                if (this.config.isReadOnly && partialPages.length > 0) {
                    //TODO: do we need another snapshot for csave after this?
                    // currently this is only supported for read-only mode
                    // but when not in read-only, this probably needs to be handled delicately
                    return await loadAdditionalPages(partialPages)
                }
                return false
            }
            if (!isHeadless(this.config) && isNewFromTemplate()) {
                log.info('template site skipping initCSave')
                return false
            }
            this.server.setInstanceProvider(() => siteAPI.getInstance())
            const result = await initCSaveInternal(partialPages)
            snapshots.takeSnapshot(CSAVE_TAG)
            await initChannel()
            this.isInitialized = true
            return result
        }

        eventEmitter.addListener(CS_EVENTS.CSAVE.SITE_SAVED, async () => {
            if (!this.isInitialized) {
                log.info('FIRST SAVE - initCSave')
                await initCSave()
            }
        })

        eventEmitter.on(CS_EVENTS.SAVE.FINISHED_SAVING, async () => {
            if (this.pendingIndex > -1) {
                const toPendingSnapshot = snapshots.getSnapshotByTagAndIndex(CSAVE_PENDING_TAG, this.pendingIndex)
                this.pendingIndex = -1
                await saveFromLastCSaveSnapshot(toPendingSnapshot)
            }
        })

        const rejectNext = () => {
            duplexer!.rejectNext()
        }

        const testApi = new CSaveTestApi(this)
        const test = () => testApi

        const isBrokenRefAddedError = (error: DocumentServiceErrorInfo) => {
            const {errorDescription} = error
            return !!errorDescription && errorDescription.includes('transaction adds a broken ref')
        }

        const getConfigExtras = () => _.pick(this.config, ['acceptTransactionsFromDuplexer', 'cedit'])

        const wrappedHookSymbol = Symbol('wrappedHook')

        const getWrappedHook = (hook: Function, hookName: string) => {
            if (hook[wrappedHookSymbol]) return hook

            const wrappedHook = (...hookArgs: any[]) => {
                if (hookName === 'onDiffSaveFinished' && hookArgs.length) {
                    const error = hookArgs[0]?.document ?? hookArgs[0]?.documentServices
                    logger.captureError(
                        new ReportableError({
                            errorType: _.get(error, ['errorType'], 'unknownSaveError'),
                            message: 'save diff was called with error',
                            tags: {saveFailure: 'onDiffSaveFinished'},
                            extras: {
                                originalError: JSON.stringify(error),
                                cSaveError: isBrokenRefAddedError(error),
                                ...getConfigExtras()
                            }
                        })
                    )
                }
                try {
                    hook(...hookArgs)
                } catch (e: any) {
                    logger.captureError(e, {tags: {csaveHook: true}, extras: {hookName}})
                }
            }

            wrappedHook[wrappedHookSymbol] = true

            return wrappedHook
        }

        const getWrappedHooks = (saveHooks: CSaveHooks) => {
            const callbacks = {}

            _.forEach(saveHooks, (val, key) => {
                if (_.isFunction(val)) {
                    callbacks[key] = getWrappedHook(val, key)
                }
            })

            return callbacks
        }

        const getActionCount = () => {
            const snap = snapshots.getCurrentSnapshot()
            if (!snap) {
                return 0
            }
            const lastCsaveSnap = getLastCsaveSnapshot(snapshots)
            const chain = createSnapshotChain(lastCsaveSnap, snap, 'getActionCount')
            return _.sum(chain.map(s => s.getStore().size()))
        }

        const getLastSaveDate = async (): Promise<Date | undefined> => {
            const lastSaveDate = this.server.getLastSaveDate()
            if (lastSaveDate) {
                return lastSaveDate
            }

            const lastTransactionId = this.server.getLast()
            if (lastTransactionId) {
                const tx = await this.server.getTransaction(lastTransactionId, dsSiteApi.getBranchId(), fragmentQuery)
                return tx?.transaction?.dateCreated || undefined
            }
        }

        const waitForCurrentPendingCsaves = (): Promise<void> => notifier.waitForCurrentPending()

        return {
            continuousSave: {
                createRevision,
                save,
                saveAndWaitForResult,
                forceSaveAndWaitForResult,
                initFragmentsTransactions,
                initCSave,
                deleteTx: async () => {
                    const t = await this.server.deleteTransactions()
                    log.info('deleteTransaction', t)
                },
                setSaving,
                disableSaveDuringRequiredAndPrimary,
                getLastTransactionId: () => this.server.getLast(),
                getLastSaveDate,
                initHooks: (saveHooks: CSaveHooks) => {
                    const wrappedHooks = getWrappedHooks(saveHooks)
                    _.forEach(wrappedHooks, (val, key) => {
                        this.hooks[key] = val
                    })
                },
                getWrappedHooks,
                setEnabled,
                shouldSave,
                getPagesAddedInRevision,
                getPagesRemovedInRevision,
                /**
                 * for testing purposes, rejects the next transaction
                 */
                rejectNext,
                /**
                 * for rendererModel#clientSpecMap reloader, don't use for other purposes
                 */
                registerToTransactionApproved: (cb: (txStore: Store) => Promise<void>) => {
                    if (_.isFunction(transactionApprovedCb)) {
                        throw new Error('registerToTransactionApproved is not allowed twice')
                    }
                    transactionApprovedCb = cb
                },
                /**
                 * for rendererModel schema/component loading after rebase, don't use for other purposes
                 */
                registerToTransactionApprovedAfterRebase: (cb: () => Promise<void>) => {
                    if (_.isFunction(transactionApprovedAfterRebaseCb)) {
                        throw new Error('registerToTransactionApprovedAfterRebase is not allowed twice')
                    }
                    transactionApprovedAfterRebaseCb = cb
                },
                /**
                 * debug method, prints store from a transaction id
                 * @param branch
                 * @param afterTransactionId
                 * @param untilTransactionId
                 * @param fragmentTypesFilter
                 * @returns {Promise<void>}
                 */
                getStore: async (
                    branch?: string,
                    afterTransactionId?: string,
                    untilTransactionId?: string,
                    fragmentTypesFilter?: FragmentTypesFilter
                ) => {
                    return await this.server.getStore(
                        branch,
                        afterTransactionId,
                        untilTransactionId,
                        fragmentTypesFilter
                    )
                },
                //@ts-ignore
                getTransactions: async (
                    afterTransactionId?: string,
                    untilTransactionId?: string,
                    branchId?: string,
                    fragmentTypesFilter?: FragmentTypesFilter
                ) =>
                    await this.server.getTransactions(
                        afterTransactionId,
                        untilTransactionId,
                        branchId,
                        fragmentTypesFilter
                    ),
                getTransactionsFromLastRevision: async (untilTransactionId?: string, branchId?: string) => {
                    const revisionTransactionId = dal.get(
                        pointers.autoSave.getAutoSaveInnerPointer('lastTransactionId')
                    )
                    return await this.server.getTransactions(
                        revisionTransactionId,
                        untilTransactionId,
                        branchId,
                        fragmentQuery
                    )
                },
                approveForeignTransaction: async (t: GetTransactionRes) => {
                    if (this.server instanceof MockCEditTestServer) {
                        await this.server.approveForeignTransaction(t)
                    }
                },
                test,
                isCSaveOpen: () => true,
                isCEditOpen: () => !!this.config.cedit,
                waitForResponsesToBeProcessed: async () => {
                    await queue.toBeEmpty()
                },
                onRevisionChange,
                getActionCount,
                getUnappliedActionCountSinceLastRevision,
                getUnappliedTransactionCountSinceLastRevision,
                isUnappliedTransactionThresholdPassed,
                getUnappliedTransactionsData,
                cSaveErrors: {
                    convertToDSError
                },
                isLocalTransaction,
                waitForCurrentPendingCsaves,
                createRevisionWithSuppressTransactions,
                restoreRevision: async (revisionId: string, siteId: string, branchId?: string) => {
                    return await this.server.restoreRevision(revisionId, siteId, branchId)
                },
                notifySessionWillExpire: () => {
                    hooks.onRefreshRequired?.('SESSION_WILL_EXPIRE')
                }
            }
        }
    }

    async initialize({extensionAPI}: DmApis): Promise<void> {
        const {siteAPI: rmSiteApi} = extensionAPI as RMApi
        this.server.setInstanceProvider(() => rmSiteApi.getInstance())
    }
}

const createExtension = ({dsConfig, environmentContext}: CreateExtensionArgument): Extension => {
    if (dsConfig.continuousSave) {
        if (!environmentContext?.serverFacade) {
            throw new Error('Illegal attempt to register CSave without providing server facade implementation')
        }
        log = debug('csave', environmentContext.loggerDriver)
        const maxTransactionsModifier = environmentContext.csaveMaxTransactionsModifier ?? _.random(1, 50)
        return new CSaveExtension(environmentContext.serverFacade, dsConfig, maxTransactionsModifier)
    }
    return new EmptyCSaveExt()
}

export {createExtension, CSAVE_TAG, MAX_TRANSACTIONS_BASE}
